import React, { useCallback, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useGetAtolCertificateById } from '~/hooks/apis/travel-compliance/useGetAtolCertificateById';

import AtolCertificateHtmlViewer from './AtolCertificateHtmlViewer';
import { CERTIFICATE_STATUS, CERTIFICATE_STATUS_COLOR, PROCESS_STATUS_COLOR } from './atolConstants';
import { generateAtolCertificatePdfFromIframe } from './atolPdfUtils';

function getChipColor<T extends string>(map: Record<string, T>, value: string, fallback: T): T {
  return map[value] ?? fallback;
}

export default function AtolCertificateDetailPage() {
  const { id_atol_certificate: certificateId } = useParams<{ id_atol_certificate: string }>();
  const { data: certificate, isLoading, error } = useGetAtolCertificateById(certificateId);
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleDownloadPdf = useCallback(async () => {
    if (!certificate || !iframeRef.current) {
      return;
    }
    setIsDownloading(true);
    try {
      const filename = `ATOL-${certificate.uniqueReferenceNumber}-v${certificate.version}.pdf`;
      await generateAtolCertificatePdfFromIframe(iframeRef.current, filename);
    } catch (err) {
      enqueueSnackbar('Failed to generate PDF. Please try again.', { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  }, [certificate, enqueueSnackbar]);

  return (
    <Container maxWidth="xl">
      {!isLoading && certificate && (
        <PageHeader
          title="ATOL Certificate"
          subtitle={
            <Typography variant="h4">{`${certificate.uniqueReferenceNumber} - Version ${certificate.version}`}</Typography>
          }
          backButton
        />
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={48} />
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message || 'Failed to load certificate details'}
        </Alert>
      )}

      {!isLoading && !error && !certificate && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Certificate not found
        </Alert>
      )}

      {certificate && (
        <>
          <Card sx={{ mt: 2 }}>
            <CardHeader title={<Typography variant="h2">Certificate Details</Typography>} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Customer Email
                  </Typography>
                  <Typography variant="body2">
                    <Link
                      href={`/users/${certificate.customerId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {certificate.customerEmail}
                      <OpenInNewIcon fontSize="inherit" />
                    </Link>
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Covered Price
                  </Typography>
                  <Typography variant="body2">
                    {certificate.currency} {certificate.coveredPrice}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Version
                  </Typography>
                  <Typography variant="body2">{certificate.version}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Versions
                  </Typography>
                  <Typography variant="body2">{certificate.totalVersions}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2">{dayjs(certificate.createdAt).format('DD MMM YYYY HH:mm:ss')}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Country Code
                  </Typography>
                  <Typography variant="body2">{certificate.countryCode}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Currency Code
                  </Typography>
                  <Typography variant="body2">{certificate.currency}</Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Certificate Status
                  </Typography>
                  <Box>
                    <Chip
                      label={certificate.certificateStatus}
                      size="small"
                      color={getChipColor(CERTIFICATE_STATUS_COLOR, certificate.certificateStatus, 'default')}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Process Status
                  </Typography>
                  <Box>
                    <Chip
                      label={certificate.processStatus}
                      size="small"
                      color={getChipColor(PROCESS_STATUS_COLOR, certificate.processStatus, 'default')}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {certificate.certificateHtml && (
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Tooltip
                    title={
                      certificate.certificateStatus !== CERTIFICATE_STATUS.ACTIVE
                        ? `Editing is disabled because the certificate status is ${certificate.certificateStatus}. Only ACTIVE certificates can be edited.`
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<EditIcon />}
                        href={`/atol-certificate/${certificateId}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={certificate.certificateStatus !== CERTIFICATE_STATUS.ACTIVE}
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={isDownloading ? <Spinner size={20} color="grey" /> : <DownloadIcon />}
                    disabled={isDownloading}
                    onClick={handleDownloadPdf}
                  >
                    {isDownloading ? 'Generating PDF...' : 'Download PDF'}
                  </Button>
                </Stack>
              </CardContent>
              <CardContent>
                <AtolCertificateHtmlViewer ref={iframeRef} certificateHtml={certificate.certificateHtml} />
              </CardContent>
            </Card>
          )}

          {(certificate.relatedOrderIds.length > 0 || certificate.relatedOfflineFlightsPnrs.length > 0) && (
            <Card>
              <CardHeader title={<Typography variant="h2">Related Orders</Typography>} />
              <CardContent>
                <List disablePadding>
                  {certificate.relatedOrderIds.map((orderId) => (
                    <ListItem key={orderId} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <OpenInNewIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Link
                            href={`/purchases/${orderId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                          >
                            {orderId}
                          </Link>
                        }
                      />
                    </ListItem>
                  ))}
                  {certificate.relatedOfflineFlightsPnrs.map((pnr) => (
                    <ListItem key={pnr} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <AirplanemodeActiveIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText primary={`Offline Flight - Provider PNR: ${pnr}`} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Container>
  );
}
